import type { Point2D } from '@shared/models/geometry.model';

/**
 * Largest allowed distance between two points in the same shape.
 * Near-parallel snap and edge intersections divide by a tiny cross product and
 * would otherwise place a vertex arbitrarily far away.
 */
export const MAX_POINT_SEPARATION_METERS = 5000;

const isFinitePoint = (point: Point2D | undefined): point is Point2D =>
  !!point && Number.isFinite(point[0]) && Number.isFinite(point[1]);

const distanceMeters = (first: Point2D, second: Point2D): number =>
  Math.hypot(second[0] - first[0], second[1] - first[1]);

/**
 * True when {@link point} is non-finite or farther than
 * {@link MAX_POINT_SEPARATION_METERS} from every point in {@link others}.
 * An empty {@link others} list only rejects non-finite coordinates.
 */
export const isPointBeyondMaxSeparation = (
  point: Point2D,
  others: readonly Point2D[],
): boolean => {
  if (!Number.isFinite(point[0]) || !Number.isFinite(point[1])) return true;
  if (!others.length) return false;

  return others.every(
    (other) => distanceMeters(point, other) > MAX_POINT_SEPARATION_METERS,
  );
};

/**
 * Keep {@link preferred} when it stays within {@link MAX_POINT_SEPARATION_METERS}
 * of every anchor. Otherwise keep {@link fallback}. Returns undefined when both
 * are non-finite or too far. An empty anchor list only rejects non-finite points.
 */
export const preferPointWithinMaxSeparation = (
  preferred: Point2D,
  fallback: Point2D,
  anchors: readonly Point2D[],
): Point2D | undefined => {
  if (!isFartherThanMaxFromAnyPoint(preferred, anchors)) return preferred;
  const samePoint =
    preferred[0] === fallback[0] && preferred[1] === fallback[1];
  if (!samePoint && !isFartherThanMaxFromAnyPoint(fallback, anchors)) {
    return fallback;
  }
  return undefined;
};

/**
 * True when {@link point} is non-finite or farther than
 * {@link MAX_POINT_SEPARATION_METERS} from any point in {@link others}.
 */
export const isFartherThanMaxFromAnyPoint = (
  point: Point2D,
  others: readonly Point2D[],
): boolean => {
  if (!isFinitePoint(point)) return true;
  return others.some(
    (other) => distanceMeters(point, other) > MAX_POINT_SEPARATION_METERS,
  );
};

/**
 * True when a drag pushes a moved vertex farther from another vertex and past
 * {@link MAX_POINT_SEPARATION_METERS}. A vertex that is already too far may
 * move closer. Pairs that did not move are ignored, so one flung corner does
 * not freeze the rest of the shape.
 */
export const dragIncreasesPointSeparation = (
  before: readonly Point2D[],
  after: readonly Point2D[],
): boolean => {
  for (let index = 0; index < after.length; index += 1) {
    const next = after[index];
    if (!isFinitePoint(next)) return true;
    const previous = before[index];
    if (!previous || !isFinitePoint(previous)) {
      const others = after.filter((_, other) => other !== index);
      if (isFartherThanMaxFromAnyPoint(next, others)) return true;
      continue;
    }
    const moved = previous[0] !== next[0] || previous[1] !== next[1];
    if (!moved) continue;

    for (let other = 0; other < after.length; other += 1) {
      if (other === index) continue;
      const otherAfter = after[other];
      if (!isFinitePoint(otherAfter)) return true;
      const nextDistance = distanceMeters(next, otherAfter);
      if (!(nextDistance > MAX_POINT_SEPARATION_METERS)) continue;
      const otherBefore = before[other];
      const previousDistance =
        otherBefore && isFinitePoint(otherBefore)
          ? distanceMeters(previous, otherBefore)
          : 0;
      // Ignore sub-millimetre growth so a closer drag is not rejected by noise.
      if (nextDistance > previousDistance + 0.001) return true;
    }
  }
  return false;
};

/**
 * True when any vertex is non-finite, or any pair is farther apart than
 * {@link MAX_POINT_SEPARATION_METERS}.
 */
export const shapePointsExceedMaxSeparation = (
  points: readonly Point2D[],
): boolean => {
  for (let i = 0; i < points.length; i += 1) {
    const start = points[i];
    if (!isFinitePoint(start)) return true;
    for (let j = i + 1; j < points.length; j += 1) {
      const end = points[j];
      if (!isFinitePoint(end)) return true;
      if (distanceMeters(start, end) > MAX_POINT_SEPARATION_METERS) return true;
    }
  }
  return false;
};

/**
 * Intersection of two infinite lines.
 * Returns undefined when the lines are parallel or the intersection would sit
 * farther than {@link MAX_POINT_SEPARATION_METERS} from any endpoint.
 */
export const findInfiniteLineIntersection = (
  firstStart: Point2D,
  firstEnd: Point2D,
  secondStart: Point2D,
  secondEnd: Point2D,
): Point2D | undefined => {
  const firstDx = firstEnd[0] - firstStart[0];
  const firstDy = firstEnd[1] - firstStart[1];
  const secondDx = secondEnd[0] - secondStart[0];
  const secondDy = secondEnd[1] - secondStart[1];
  const denominator = firstDx * secondDy - firstDy * secondDx;
  // Parallel lines, including a zero-length edge. Dividing here is the runaway.
  if (!(Math.abs(denominator) > 0)) return undefined;

  const startDx = secondStart[0] - firstStart[0];
  const startDy = secondStart[1] - firstStart[1];
  const firstRatio = (startDx * secondDy - startDy * secondDx) / denominator;
  if (!Number.isFinite(firstRatio)) return undefined;

  const intersection: Point2D = [
    firstStart[0] + firstRatio * firstDx,
    firstStart[1] + firstRatio * firstDy,
  ];
  if (
    isFartherThanMaxFromAnyPoint(intersection, [
      firstStart,
      firstEnd,
      secondStart,
      secondEnd,
    ])
  ) {
    return undefined;
  }
  return intersection;
};

/**
 * Intersection of two infinite lines defined by an anchor and a direction in degrees.
 * Returns undefined when the lines are parallel or the intersection is farther
 * than {@link MAX_POINT_SEPARATION_METERS} from either anchor.
 */
export const intersectAngleLines = (
  anchor1: Point2D,
  angle1Degrees: number,
  anchor2: Point2D,
  angle2Degrees: number,
): Point2D | undefined => {
  const rad1 = (angle1Degrees * Math.PI) / 180;
  const rad2 = (angle2Degrees * Math.PI) / 180;
  const d1x = Math.cos(rad1);
  const d1y = Math.sin(rad1);
  const d2x = Math.cos(rad2);
  const d2y = Math.sin(rad2);
  const cross = d1x * d2y - d1y * d2x;
  if (!(Math.abs(cross) > 0)) return undefined;

  const [x1, y1] = anchor1;
  const [x2, y2] = anchor2;
  const t = ((x2 - x1) * d2y - (y2 - y1) * d2x) / cross;
  if (!Number.isFinite(t)) return undefined;

  const intersection: Point2D = [x1 + t * d1x, y1 + t * d1y];
  if (isFartherThanMaxFromAnyPoint(intersection, [anchor1, anchor2])) {
    return undefined;
  }
  return intersection;
};

const clonePoint = (point: Point2D): Point2D => [point[0], point[1]];

const centroidOf = (points: readonly Point2D[]): Point2D => {
  const finite = points.filter(isFinitePoint);
  if (!finite.length) return [0, 0];
  const sum = finite.reduce(
    (total, point) => [total[0] + point[0], total[1] + point[1]] as Point2D,
    [0, 0] as Point2D,
  );
  return [sum[0] / finite.length, sum[1] / finite.length];
};

const farthestPairDistance = (points: readonly Point2D[]): number => {
  let farthest = 0;
  for (let i = 0; i < points.length; i += 1) {
    const start = points[i];
    if (!isFinitePoint(start)) continue;
    for (let j = i + 1; j < points.length; j += 1) {
      const end = points[j];
      if (!isFinitePoint(end)) continue;
      farthest = Math.max(farthest, distanceMeters(start, end));
    }
  }
  return farthest;
};

/**
 * Shrink {@link points} around their centroid until every pair is within
 * {@link MAX_POINT_SEPARATION_METERS}.
 */
const scalePointsWithinMaxSeparation = (
  points: readonly Point2D[],
): Point2D[] => {
  const center = centroidOf(points);
  const farthest = farthestPairDistance(points);
  if (!(farthest > MAX_POINT_SEPARATION_METERS)) {
    return points.map(clonePoint);
  }
  // Stay strictly inside the limit when floating point rounds the chord back up.
  const scale = (MAX_POINT_SEPARATION_METERS / farthest) * (1 - 1e-9);
  return points.map((point) => [
    center[0] + (point[0] - center[0]) * scale,
    center[1] + (point[1] - center[1]) * scale,
  ]);
};

/**
 * Move {@link outlier} toward {@link center} until it is within
 * {@link MAX_POINT_SEPARATION_METERS} of every core point.
 */
const pullTowardCenter = (
  outlier: Point2D,
  core: readonly Point2D[],
  center: Point2D,
): Point2D => {
  const start = isFinitePoint(outlier) ? outlier : center;
  let low = 0;
  let high = 1;
  let best = center;
  for (let step = 0; step < 40; step += 1) {
    const t = (low + high) / 2;
    const candidate: Point2D = [
      start[0] + (center[0] - start[0]) * t,
      start[1] + (center[1] - start[1]) * t,
    ];
    if (
      core.every(
        (point) =>
          distanceMeters(candidate, point) <= MAX_POINT_SEPARATION_METERS,
      )
    ) {
      best = candidate;
      high = t;
    } else {
      low = t;
    }
  }
  return best;
};

const pointsAt = (
  points: readonly Point2D[],
  indexes: readonly number[],
): Point2D[] =>
  indexes.flatMap((index) => {
    const point = points[index];
    return point ? [point] : [];
  });

/** Index of the vertex farthest from the centroid of {@link points}. */
const indexFarthestFromCentroid = (points: readonly Point2D[]): number => {
  const center = centroidOf(points);
  return points.reduce((farthestIndex, point, index) => {
    const farthest = points[farthestIndex];
    if (!farthest) return index;
    return distanceMeters(point, center) > distanceMeters(farthest, center)
      ? index
      : farthestIndex;
  }, 0);
};

/**
 * Drop vertices farthest from the centroid until the remainder is within
 * {@link MAX_POINT_SEPARATION_METERS}, keeping at least three vertices.
 * Returns undefined when even three vertices are too far apart.
 */
const findSeparationCoreIndexes = (
  points: readonly Point2D[],
): number[] | undefined => {
  const remaining = points.map((_, index) => index);
  while (
    shapePointsExceedMaxSeparation(pointsAt(points, remaining)) &&
    remaining.length > 3
  ) {
    const localIndex = indexFarthestFromCentroid(pointsAt(points, remaining));
    remaining.splice(localIndex, 1);
  }
  if (shapePointsExceedMaxSeparation(pointsAt(points, remaining))) {
    return undefined;
  }
  return remaining;
};

/**
 * Move vertices outside {@link coreIndexes} toward the core. Each moved
 * vertex must also stay within {@link MAX_POINT_SEPARATION_METERS} of
 * vertices already placed, so two opposite runaways cannot stay apart.
 */
const pullOutliersTowardCore = (
  points: readonly Point2D[],
  coreIndexes: readonly number[],
): Point2D[] => {
  const core = pointsAt(points, coreIndexes);
  const coreCenter = centroidOf(core);
  const coreIndexSet = new Set(coreIndexes);
  const placed = [...core];
  return points.map((point, index) => {
    if (coreIndexSet.has(index)) return point;
    const next = pullTowardCenter(point, placed, coreCenter);
    placed.push(next);
    return next;
  });
};

/**
 * Move vertices that sit beyond {@link MAX_POINT_SEPARATION_METERS} back
 * inside that limit. Runaway vertices are peeled off until a core of at
 * least three vertices fits, then pulled toward that core. A footprint with
 * no such core is scaled around its centroid. Returns the same array when
 * every pair is already inside the limit.
 */
export const pullShapePointsWithinMaxSeparation = (
  points: readonly Point2D[],
): readonly Point2D[] => {
  if (!shapePointsExceedMaxSeparation(points)) return points;

  const finite = points.filter(isFinitePoint);
  const center = centroidOf(finite);
  const sanitized = points.map((point) =>
    isFinitePoint(point) ? clonePoint(point) : center,
  );
  if (!shapePointsExceedMaxSeparation(sanitized)) return sanitized;

  const coreIndexes = findSeparationCoreIndexes(sanitized);
  if (!coreIndexes) return scalePointsWithinMaxSeparation(sanitized);

  const pulled = pullOutliersTowardCore(sanitized, coreIndexes);
  if (!shapePointsExceedMaxSeparation(pulled)) return pulled;

  // A pairwise miss after the pull must not scale the valid core.
  const coreCenter = centroidOf(pointsAt(sanitized, coreIndexes));
  const coreIndexSet = new Set(coreIndexes);
  return sanitized.map((point, index) =>
    coreIndexSet.has(index) ? point : clonePoint(coreCenter),
  );
};
